/**
 * SaveTransformBar — Conditional footer bar for saving toolbar-composed SQL.
 *
 * Always creates a new transform operation on top of the current node.
 * Appears only when the SQL has been modified via the TransformBar.
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useCreateOperation } from '../../hooks/useWorkspaceSession';
import type { WorkspaceTable } from '../../services/workspaceApi';
import { useNotifications } from '../../context/NotificationContext';
import { generateOutputName, extractTableRefs, summarizeChanges } from './sqlBuilder';
import type { TransformState } from './sqlBuilder';

// ── Types ────────────────────────────────────────────────────────────

export interface SaveTransformBarProps {
  sessionId: string;
  currentSql: string;
  transformState: TransformState | null;
  isModified: boolean;
  /** null = exploration mode (source node or no operation selected) */
  operationContext?: {
    operationId: string;
    operationType: string;
    config: Record<string, any>;
  } | null;
  workspaceTables: WorkspaceTable[];
  onOperationCreated?: (operationId: string) => void;
  onOperationUpdated?: () => void;
}

// ── Component ────────────────────────────────────────────────────────

const SaveTransformBar: React.FC<SaveTransformBarProps> = ({
  sessionId,
  currentSql,
  transformState,
  isModified,
  operationContext,
  workspaceTables,
  onOperationCreated,
}) => {
  const [outputName, setOutputName] = useState(() => generateOutputName(workspaceTables));
  const [exploratory, setExploratory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createOp = useCreateOperation(sessionId);
  const { showToast } = useNotifications();

  const changeSummary = transformState ? summarizeChanges(transformState) : '';

  // Auto-generate output name when workspace tables change
  const defaultName = useMemo(() => generateOutputName(workspaceTables), [workspaceTables]);

  // ── Save as new Transform (always creates new operation) ──────────

  const handleSaveAsTransform = useCallback(async () => {
    if (!currentSql.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const inputRefs = extractTableRefs(currentSql, workspaceTables);
      const result = await createOp.mutateAsync({
        operation_type: 'sql_transform',
        config: { sql: currentSql },
        output_name: outputName || defaultName,
        input_refs: inputRefs,
        exploratory,
      });
      showToast('Transform Saved', 'Successfully created new transform operation.', 'success');
      onOperationCreated?.(result.operation_id);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to create transform');
    } finally {
      setLoading(false);
    }
  }, [currentSql, outputName, defaultName, exploratory, workspaceTables, createOp, onOperationCreated]);

  if (!isModified) return null;

  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: '#f0f7ff',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      {changeSummary && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
          {changeSummary}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
        Output:
      </Typography>
      <TextField
        size="small"
        value={outputName}
        onChange={(e) => setOutputName(e.target.value)}
        placeholder={defaultName}
        sx={{
          width: 160,
          '& input': { fontSize: 12, fontFamily: '"Fira Code", monospace', py: 0.5, px: 1 },
        }}
      />
      {!operationContext && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={exploratory}
              onChange={(e) => setExploratory(e.target.checked)}
              sx={{ py: 0, '& .MuiSvgIcon-root': { fontSize: 16 } }}
            />
          }
          label={
            <Typography variant="caption" sx={{ fontSize: 11 }}>
              Exploratory
            </Typography>
          }
          sx={{ mr: 0 }}
        />
      )}
      <Box sx={{ flex: 1 }} />
      <Button
        size="small"
        variant="contained"
        onClick={handleSaveAsTransform}
        disabled={loading || !currentSql.trim() || !outputName.trim()}
        sx={{ fontSize: 11, textTransform: 'none', py: 0.25, px: 1.5 }}
      >
        {loading ? <CircularProgress size={14} color="inherit" /> : 'Save as Transform'}
      </Button>

      {/* Error display */}
      {error && (
        <Typography variant="caption" color="error" sx={{ fontSize: 11, width: '100%', mt: 0.25 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default SaveTransformBar;
